import  { useState } from 'react';
import PremadeCombo from './PremadeCombo';
import CustomBuild from './Custom';
import InsemNotes from './InsemNotes';
import useLookupData from '../../services/lookupData';

// ✅ Simple placeholder icons
const CombosIcon = ({ className }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    viewBox="0 0 24 24"
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 12h18M3 6h18M3 18h18" />
  </svg>
);

const CustomBuildIcon = ({ className }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 4v16m8-8H4"
    />
  </svg>
);

const InsemIcon = ({ className }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
    />
  </svg>
);

const CustomNotes = ({ year, branch, semester, subject, type, shouldFetch, searchTrigger }) => {
  const [activeView, setActiveView] = useState('pre-made');
  
  // Use the custom hook to fetch lookup data only when shouldFetch is true
  const { lookup } = useLookupData({ year, branch, semester, subject, type, shouldFetch, searchTrigger });
  // console.log(year, branch, subject)
  // console.log("custom notes - lookup data:", lookup)
 
  // Show placeholder UI if search hasn't been performed yet
  if (!shouldFetch) {
    return (
      <div className="w-full bg-transparent text-gray-200 antialiased">
        <div className="container mx-auto px-4 py-12 md:py-20">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-8">
              
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Start Your Search
              </h2>
              <p className="text-lg text-gray-400 max-w-2xl mx-auto">
                Select your course details above and click "Search Material" to discover available study materials and packages.
              </p>
            </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-12">
              <div className="bg-gradient-to-br from-gray-900/60 to-gray-900/40 border border-gray-800 rounded-xl p-6 hover:border-indigo-500/50 transition-all">
                <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-indigo-500/10 border border-indigo-500/20 mb-4 mx-auto">
                  <svg className="w-6 h-6 text-indigo-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 12h18M3 6h18M3 18h18" />
                  </svg>
                </div>
                <h3 className="text-white font-semibold text-center mb-2">Complete Packages</h3>
                <p className="text-sm text-gray-400 text-center">Full semester subject combo or entire branch combo at discounted prices</p>
              </div>

              <div className="bg-gradient-to-br from-gray-900/60 to-gray-900/40 border border-gray-800 rounded-xl p-6 hover:border-purple-500/50 transition-all">
                <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-purple-500/10 border border-purple-500/20 mb-4 mx-auto">
                  <svg className="w-6 h-6 text-purple-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                  </svg>
                </div>
                <h3 className="text-white font-semibold text-center mb-2">Select Units</h3>
                <p className="text-sm text-gray-400 text-center">Choose specific units from the selected subject as per your needs</p>
              </div>

              <div className="bg-gradient-to-br from-gray-900/60 to-gray-900/40 border border-gray-800 rounded-xl p-6 hover:border-green-500/50 transition-all">
                <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-green-500/10 border border-green-500/20 mb-4 mx-auto">
                  <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                  </svg>
                </div>
                <h3 className="text-white font-semibold text-center mb-2">Insem Notes</h3>
                <p className="text-sm text-gray-400 text-center">Free downloadable insem notes and study materials</p>
              </div>

              <div className="bg-gradient-to-br from-gray-900/60 to-gray-900/40 border border-gray-800 rounded-xl p-6 hover:border-pink-500/50 transition-all">
                <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-pink-500/10 border border-pink-500/20 mb-4 mx-auto">
                  <svg className="w-6 h-6 text-pink-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <h3 className="text-white font-semibold text-center mb-2">View Demos</h3>
                <p className="text-sm text-gray-400 text-center">Preview materials before making a purchase</p>
              </div>
            </div>            <div className="mt-12 p-6 rounded-xl bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-pink-500/10 border border-indigo-500/20">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 mt-1">
                  <svg className="w-6 h-6 text-indigo-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h4 className="text-white font-semibold mb-2">How it works</h4>
                  <ol className="text-sm text-gray-300 space-y-2">
                    <li className="flex items-start gap-2">
                      <span className="flex-shrink-0 flex items-center justify-center w-5 h-5 rounded-full bg-indigo-500/20 text-indigo-400 text-xs font-bold">1</span>
                      <span>Fill in your course details in the form above</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="flex-shrink-0 flex items-center justify-center w-5 h-5 rounded-full bg-indigo-500/20 text-indigo-400 text-xs font-bold">2</span>
                      <span>Click the "Search Material" button to find available content</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="flex-shrink-0 flex items-center justify-center w-5 h-5 rounded-full bg-indigo-500/20 text-indigo-400 text-xs font-bold">3</span>
                      <span>Choose complete packages (semester/branch) or select specific units</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="flex-shrink-0 flex items-center justify-center w-5 h-5 rounded-full bg-indigo-500/20 text-indigo-400 text-xs font-bold">4</span>
                      <span>Preview demos and purchase the materials you need</span>
                    </li>
                  </ol>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className=" w-full bg-transparent text-gray-200 antialiased"
      
    >
      <div className="container mx-auto px-4 py-12 md:py-20">
        {/* Header */}
        <header className="text-center mb-10">
          <h1 className="text-4xl md:text-5xl font-bold text-white tracking-tight">
            Choose Your Package
          </h1>
          <p className="mt-4 text-lg text-gray-400 max-w-2xl mx-auto">
            Get complete semester or branch packages, or select individual units from your subject.
          </p>
        </header>

        {/* Selected Criteria Display removed per request */}


        {/* View Toggle Buttons */}
        <main>
          <div className="flex flex-wrap justify-center items-center gap-3 mb-10">
            <button
              onClick={() => setActiveView('pre-made')}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-sm transition-all duration-300 ease-in-out transform hover:scale-105 ${
                activeView === 'pre-made'
                  ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg shadow-indigo-500/30'
                  : 'bg-gray-700/50 text-gray-300 hover:bg-gray-700/80'
              }`}
            >
              <CombosIcon className="w-4 h-4" />
              Complete Packages
            </button>

            <button
              onClick={() => setActiveView('custom')}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-sm transition-all duration-300 ease-in-out transform hover:scale-105 ${
                activeView === 'custom'
                  ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg shadow-indigo-500/30'
                  : 'bg-gray-700/50 text-gray-300 hover:bg-gray-700/80'
              }`}
            >
              <CustomBuildIcon className="w-4 h-4" />
              Select Units
            </button>

            <button
              onClick={() => setActiveView('insem')}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-sm transition-all duration-300 ease-in-out transform hover:scale-105 ${
                activeView === 'insem'
                  ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg shadow-indigo-500/30'
                  : 'bg-gray-700/50 text-gray-300 hover:bg-gray-700/80'
              }`}
            >
              <InsemIcon className="w-4 h-4" />
              Insem Notes
            </button>
          </div>

          {/* Conditional View Rendering */}
          <div className="max-w-4xl mx-auto">
            {activeView === 'pre-made' ? (
              <PremadeCombo 
                subjectData={lookup?.subject} 
                branchData={lookup?.branch}
                year={year}
                branch={branch}
                semester={semester}
                subject={subject}
              />
            ) : activeView === 'custom' ? (
              <CustomBuild 
                units={lookup?.units || []}
                year={year}
                branch={branch}
                semester={semester}
                subject={subject}
              />
            ) : (
              <InsemNotes
                year={year}
                branch={branch}
                semester={semester}
                subject={subject}
                type={type}
              />
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default CustomNotes;
